import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ServerIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClockIcon,
  ShieldCheckIcon,
  FireIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { companiesService, Company } from '../services/companies';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import RiskDashboard from '../components/RiskDashboard';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../services/users';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/i18nFormat';

interface DashboardOverview {
  assets: {
    total: number;
    active: number;
    monitoring: number;
  };
  events: {
    total: number;
    last_24h: number;
    critical: number;
    high: number;
  };
  rules: {
    active: number;
  };
  alerts: {
    critical: number;
    pending: number;
  };
}

interface EventDistribution {
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  by_status: Record<string, number>;
  period_days: number;
}

interface AssetDistribution {
  by_type: Record<string, number>;
  by_status: Record<string, number>;
}

interface RecentEvent {
  id: number;
  title: string;
  type: string;
  severity: string;
  status: string;
  source: string;
  created_at: string;
  confidence_score: number;
  risk_score: number;
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userCompany, setUserCompany] = useState<Company | null>(null);

  // Para usuários CLIENT, usar automaticamente a empresa do usuário
  const isClientUser = user?.role === UserRole.CLIENT;
  const domainFilter = isClientUser && userCompany ? userCompany.domain : undefined;

  // Carregar dados da empresa do usuário se for CLIENT
  useEffect(() => {
    const loadUserCompany = async () => {
      if (isClientUser && user?.company_id) {
        try {
          const company = await companiesService.getById(user.company_id);
          setUserCompany(company);
        } catch (error) {
          console.error('Erro ao carregar empresa do usuário:', error);
        }
      }
    };

    loadUserCompany();
  }, [isClientUser, user?.company_id]);

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['dashboard-overview', domainFilter],
    queryFn: async () => {
      const params = domainFilter ? `?domain=${encodeURIComponent(domainFilter)}` : '';
      const response = await api.get(`/api/v2/dashboard/overview${params}`);
      return response.data as DashboardOverview;
    },
  });

  const { data: eventDistribution, isLoading: eventsLoading } = useQuery({
    queryKey: ['dashboard-events-distribution', domainFilter],
    queryFn: async () => {
      const baseParams = 'active_only=false&align_with_overview=true&days=7';
      const params = domainFilter ? `?domain=${encodeURIComponent(domainFilter)}&${baseParams}` : `?${baseParams}`;
      const response = await api.get(`/api/v2/dashboard/events/distribution${params}`);
      return response.data as EventDistribution;
    },
  });

  const { data: assetDistribution, isLoading: assetsLoading } = useQuery({
    queryKey: ['dashboard-assets-distribution', domainFilter],
    queryFn: async () => {
      const params = domainFilter ? `?domain=${encodeURIComponent(domainFilter)}` : '';
      const response = await api.get(`/api/v2/dashboard/assets/distribution${params}`);
      return response.data as AssetDistribution;
    },
  });

  const { data: recentEvents, isLoading: recentEventsLoading } = useQuery({
    queryKey: ['dashboard-recent-events', domainFilter],
    queryFn: async () => {
      const params = domainFilter ? `?domain=${encodeURIComponent(domainFilter)}&limit=10` : '?limit=10';
      const response = await api.get(`/api/v2/dashboard/recent-events${params}`);
      const raw = response.data?.events ?? [];
      return raw.map((e: any) => ({
        id: e.id,
        title: e.title,
        type: e.event_type ?? e.type ?? 'UNKNOWN',
        severity: e.severity ?? 'UNKNOWN',
        status: e.status ?? 'UNKNOWN',
        source: e.source ?? '—',
        created_at: e.created_at ?? new Date().toISOString(),
        confidence_score: e.confidence_score ?? 0,
        risk_score: e.risk_score ?? 0,
      })) as RecentEvent[];
    },
  });

  if (overviewLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  const statCards = [
    {
      name: t('dashboard.stats.assetsTotal'),
      value: overview?.assets.total || 0,
      subValue: t('dashboard.stats.assetsSub', { count: overview?.assets.active || 0 }),
      icon: ServerIcon,
      accent: 'text-blue-500 dark:text-blue-400',
      border: 'border-l-blue-500',
    },
    {
      name: t('dashboard.stats.events24h'),
      value: overview?.events.last_24h || 0,
      subValue: t('dashboard.stats.eventsSubTotal', { count: overview?.events.total || 0 }),
      icon: ClockIcon,
      accent: 'text-amber-500 dark:text-amber-400',
      border: 'border-l-amber-500',
    },
    {
      name: t('dashboard.stats.criticalEvents'),
      value: overview?.events.critical || 0,
      subValue: t('dashboard.stats.criticalSubHigh', { count: overview?.events.high || 0 }),
      icon: FireIcon,
      accent: 'text-red-500 dark:text-red-400',
      border: 'border-l-red-500',
    },
    {
      name: t('dashboard.stats.activeRules'),
      value: overview?.rules.active || 0,
      subValue: t('dashboard.stats.rulesSubAutomations'),
      icon: ShieldCheckIcon,
      accent: 'text-emerald-500 dark:text-emerald-400',
      border: 'border-l-emerald-500',
    },
  ];

  // Narrativa semanal (Resumo)
  const periodDays = eventDistribution?.period_days ?? 7;
  const severityCounts = eventDistribution?.by_severity || {};
  const criticalCount = severityCounts['CRITICAL'] || 0;
  const highCount = severityCounts['HIGH'] || 0;
  const totalRecent = Object.values(severityCounts).reduce((acc, val) => acc + (val || 0), 0);
  const topTypes = Object.entries(eventDistribution?.by_type || {})
    .sort((a, b) => (b[1] as number) - (a[1] as number))
    .slice(0, 3);
  const highlights = (recentEvents || [])
    .filter((e) => e.severity === 'CRITICAL' || e.severity === 'HIGH')
    .slice(0, 3);
  const recommendations: string[] = [
    criticalCount > 0 ? t('dashboard.recommendations.criticalMitigation') : null,
    highCount > 0 ? t('dashboard.recommendations.highSeverityActions') : null,
    (overview?.alerts.pending || 0) > 0
      ? t('dashboard.recommendations.reducePendingAlerts', { count: overview?.alerts.pending })
      : null,
    topTypes.length > 0
      ? t('dashboard.recommendations.followTopCategories', {
          categories: topTypes.map((tp) => tp[0].toLowerCase()).join(', '),
        })
      : null,
  ].filter(Boolean) as string[];

  const statusLabelKey: Record<string, string> = {
    ACTIVE: 'common.active',
    INACTIVE: 'common.inactive',
    MONITORING: 'common.monitoring',
  };

  const severityBadgeClass: Record<string, string> = {
    CRITICAL: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    HIGH:     'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    MEDIUM:   'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
    LOW:      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  };

  const renderEventsDistribution = () => (
    <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide'>{t('dashboard.eventsDistribution.title')}</h3>
        <ChartBarIcon className='h-4 w-4 text-gray-400' />
      </div>
      {eventsLoading ? (
        <div className='flex justify-center py-6'>
          <LoadingSpinner />
        </div>
      ) : (
        <div className='divide-y divide-gray-100 dark:divide-gray-700'>
          {Object.entries(eventDistribution?.by_severity || {}).map(([severity, count]) => (
            <div key={severity} className='flex items-center justify-between py-2'>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${severityBadgeClass[severity] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                {t(`common.severity.${severity.toLowerCase()}`)}
              </span>
              <span className='text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums'>{count as number}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const statusDot: Record<string, string> = {
    ACTIVE:     'bg-emerald-500',
    INACTIVE:   'bg-gray-400',
    MONITORING: 'bg-blue-500',
  };

  const renderAssetsDistribution = () => (
    <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide'>{t('dashboard.assetsDistribution.title')}</h3>
        <ServerIcon className='h-4 w-4 text-gray-400' />
      </div>
      {assetsLoading ? (
        <div className='flex justify-center py-6'>
          <LoadingSpinner />
        </div>
      ) : (
        <div className='space-y-4'>
          <div>
            <p className='text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1'>{t('dashboard.byType')}</p>
            <div className='divide-y divide-gray-100 dark:divide-gray-700'>
              {Object.entries(assetDistribution?.by_type || {}).map(([type, count]) => (
                <div key={type} className='flex items-center justify-between py-1.5'>
                  <span className='text-xs text-gray-600 dark:text-gray-300 capitalize'>{type.toLowerCase()}</span>
                  <span className='text-xs font-semibold text-gray-900 dark:text-gray-100 tabular-nums'>{count as number}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className='text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1'>{t('dashboard.byStatus')}</p>
            <div className='divide-y divide-gray-100 dark:divide-gray-700'>
              {Object.entries(assetDistribution?.by_status || {}).map(([status, count]) => (
                <div key={status} className='flex items-center justify-between py-1.5'>
                  <div className='flex items-center gap-1.5'>
                    <span className={`h-2 w-2 rounded-full ${statusDot[status] ?? 'bg-gray-400'}`} />
                    <span className='text-xs text-gray-600 dark:text-gray-300'>
                      {t(statusLabelKey[status] ?? status.toLowerCase())}
                    </span>
                  </div>
                  <span className='text-xs font-semibold text-gray-900 dark:text-gray-100 tabular-nums'>{count as number}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderRecentActivity = () => {
    if (recentEventsLoading) {
      return (
        <div className='flex justify-center py-8'>
          <LoadingSpinner />
        </div>
      );
    }
    if (recentEvents && recentEvents.length >= 0) {
      return (
        <div className='space-y-4'>
          {/* Mini KPI strip */}
          <div className="grid grid-cols-4 gap-2 text-center">
            {[
              { label: t('dashboard.summary.period'), value: t('dashboard.summary.lastNDays', { days: periodDays }), cls: 'text-gray-900 dark:text-gray-100' },
              { label: t('dashboard.summary.eventsInPeriod'), value: totalRecent, cls: 'text-gray-900 dark:text-gray-100' },
              { label: t('common.severity.critical'), value: criticalCount, cls: 'text-red-600 dark:text-red-400' },
              { label: t('common.severity.high'), value: highCount, cls: 'text-orange-500 dark:text-orange-400' },
            ].map(({ label, value, cls }) => (
              <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded px-2 py-2">
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{label}</p>
                <p className={`text-base font-bold tabular-nums ${cls}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Highlights table */}
          {highlights.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">{t('dashboard.highlights.title')}</p>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {highlights.map((event) => (
                  <div key={event.id} className="flex items-center gap-3 py-2">
                    <span className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${severityBadgeClass[event.severity] ?? 'bg-gray-100 text-gray-600'}`}>
                      {event.severity}
                    </span>
                    <p className="text-xs text-gray-900 dark:text-gray-100 truncate flex-1">{event.title}</p>
                    <span className="shrink-0 text-xs text-gray-400 tabular-nums">{Math.round((event.confidence_score ?? 0) * 100)}%</span>
                    <span className="shrink-0 text-xs text-gray-400">{formatDate(event.created_at, { dateStyle: 'short' })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top categories */}
          {topTypes.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium mr-1">{t('dashboard.topCategories.title')}:</span>
              {topTypes.map(([type, count]) => (
                <span key={type} className="px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 tabular-nums">
                  {String(type).toLowerCase()} <span className="font-semibold">{count as number}</span>
                </span>
              ))}
            </div>
          )}

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-1">{t('dashboard.recommendations.title')}</p>
              <ul className="space-y-1">
                {recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                    <ExclamationTriangleIcon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    return (
      <div className='text-center py-8'>
        <ClockIcon className='mx-auto h-12 w-12 text-gray-400' />
        <h3 className='mt-2 text-sm font-medium text-gray-900 dark:text-gray-100'>{t('dashboard.summary.empty.title')}</h3>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            {t('dashboard.summary.empty.subtitle')}
        </p>
      </div>
    );
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-baseline justify-between'>
        <div>
          <h1 className='text-xl font-bold text-gray-900 dark:text-gray-100'>{t('Dashboard')}</h1>
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
        {statCards.map(stat => (
          <div key={stat.name} className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-4 ${stat.border} rounded-lg p-4`}>
            <div className='flex items-start justify-between'>
              <div className='min-w-0'>
                <p className='text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate'>{stat.name}</p>
                <p className='mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums'>{stat.value}</p>
                <p className='mt-0.5 text-xs text-gray-500 dark:text-gray-500 truncate'>{stat.subValue}</p>
              </div>
              <stat.icon className={`h-5 w-5 shrink-0 ml-2 mt-0.5 ${stat.accent}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
        {renderEventsDistribution()}

        {renderAssetsDistribution()}
      </div>

      {/* Análise Dinâmica de Risco Financeiro */}
      <RiskDashboard />

      {/* Recent Activity */}
      <div className='bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide'>{t('dashboard.summary.title')}</h3>
          <ClockIcon className='h-4 w-4 text-gray-400' />
        </div>
        {renderRecentActivity()}
      </div>
    </div>
  );
};

export default Dashboard;
